import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import { Component, computed, inject, input, linkedSignal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../shared/modules/lucide.module';
import { TelefonePipe } from '../../../shared/pipes/telefone.pipe';
import { slugDeServico } from '../../../shared/utils/slug.util';
import {
  DESTINO_CARTA_SERVICO,
  DESTINO_FORMULARIO_SIC,
  DESTINO_LINK_EXTERNO,
  Noticia,
} from '../models/noticia.model';
import { CatalogoPublicoService } from '../services/catalogo-publico.service';
import { NoticiasService } from '../services/noticias.service';
import { RedeCompartilhamento, redesDeCompartilhamento } from './compartilhamento-noticia';
import { DataNoticiaPipe } from './data-noticia.pipe';

const ROTA_SIC = '/sic';

/** O destino do botão de serviço: rota interna do portal ou endereço externo. */
interface DestinoBotao {
  rotulo: string;
  rota: string | null;
  href: string | null;
  novaAba: boolean;
}

@Component({
  selector: 'app-noticia',
  templateUrl: './noticia.component.html',
  styleUrls: ['./noticia.component.css'],
  imports: [NgOptimizedImage, RouterLink, LucideModule, BreadcrumbComponent, DataNoticiaPipe, TelefonePipe],
})
export class NoticiaComponent {
  private readonly noticiasService = inject(NoticiasService);
  private readonly catalogo = inject(CatalogoPublicoService);
  private readonly document = inject(DOCUMENT);

  /** O parâmetro da rota `noticias/:id`, que é o slug da notícia. */
  readonly id = input.required<string>();

  private readonly detalhe = this.noticiasService.detalhe(() => this.id());

  readonly noticia = this.detalhe.noticia;

  readonly carregando = this.detalhe.carregando;

  /** Relacionadas: da mesma categoria; sem categoria, não há o que relacionar. */
  private readonly mesmaCategoria = this.noticiasService.lista(() => {
    const categoria = this.noticia()?.categoria;
    return categoria ? { categoria: Number(categoria.id), tamanho: 4 } : null;
  });

  private readonly recentes = this.noticiasService.lista(() => ({ tamanho: 5 }));

  readonly relacionadas = computed(() =>
    this.mesmaCategoria
      .noticias()
      .filter((noticia) => noticia.slug !== this.id())
      .slice(0, 3)
  );

  readonly ultimas = computed(() =>
    this.recentes
      .noticias()
      .filter((noticia) => noticia.slug !== this.id())
      .slice(0, 4)
  );

  readonly redes = computed<RedeCompartilhamento[]>(() => {
    const noticia = this.noticia();

    if (!noticia?.compartilhamento) return [];

    return redesDeCompartilhamento(noticia.redes, this.document.location?.href ?? '', noticia.titulo);
  });

  readonly botao = computed<DestinoBotao | null>(() => {
    const botao = this.noticia()?.botao;

    if (!botao) return null;

    const rotulo = botao.desTextoChamadaNtc || 'Acessar serviço';
    const novaAba = botao.bitAbrirNovaAbaNtc;

    switch (botao.desTipoDestinoNtc) {
      case DESTINO_CARTA_SERVICO: {
        const rota = this.rotaDoServico(botao.codServicoSrv);
        if (!rota) return null;
        return rota.startsWith('/')
          ? { rotulo, rota, href: null, novaAba: false }
          : { rotulo, rota: null, href: rota, novaAba };
      }
      case DESTINO_FORMULARIO_SIC:
        return { rotulo, rota: ROTA_SIC, href: null, novaAba: false };
      case DESTINO_LINK_EXTERNO:
        return botao.desLinkAlternativoNtc
          ? { rotulo, rota: null, href: botao.desLinkAlternativoNtc, novaAba }
          : null;
      default:
        return null;
    }
  });

  readonly linkCopiado = linkedSignal({
    source: () => this.id(),
    computation: () => false,
  });

  async compartilhar(): Promise<void> {
    const noticia = this.noticia();
    if (!noticia) return;

    const url = this.document.location?.href ?? '';
    const navegador = this.document.defaultView?.navigator;

    if (navegador?.share) {
      await navegador.share({ title: noticia.titulo, text: noticia.descricao, url });
      return;
    }

    await navegador?.clipboard?.writeText(url);
    this.linkCopiado.set(true);
  }

  /** WhatsApp guardado só com dígitos; sem DDI, assume o Brasil. */
  linkWhatsapp(numero: string): string {
    const digitos = numero.replace(/\D/g, '');
    return `https://wa.me/${digitos.length <= 11 ? `55${digitos}` : digitos}`;
  }

  rota(noticia: Noticia): readonly string[] {
    return this.noticiasService.rota(noticia);
  }

  /** A carta de serviço pelo catálogo público, que já conhece o link de cada serviço publicado. */
  private rotaDoServico(codigo: number | null): string | null {
    if (!codigo) return null;

    for (const categoria of this.catalogo.categorias()) {
      const servico = categoria.servicos.find((item) => item.id === String(codigo));
      if (servico) return servico.link ?? `/${slugDeServico(servico.titulo)}`;
    }

    return null;
  }
}
